import React, { useMemo, useState } from 'react';

import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import SelectField from '@tmap/mmm-style-guide/src/fields/SelectField';
import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Remove, Add } from '@tmap/mmm-style-guide/src/Icon';
import { Collapse } from '@tmap/mmm-style-guide/src/Transition';
import { Switch } from '@tmap/mmm-style-guide/src/Switch';
import { styled } from '@tmap/mmm-style-guide/src/styled';

import analyticsLib from '../../lib/analytics';

import OptionCheckbox from './optionCheckbox';

const TruncatedTypog = styled(Typography)(() => ({
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.15px',
  whiteSpace: 'nowrap',
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
}));

const OutlinedInputBox = styled(Box, {
  shouldForwardProp: (p) => p !== 'active',
})(({ theme, active }) => ({
  border: active
    ? `1px solid ${theme.palette.secondary.light}`
    : `1px solid ${theme.palette.grey[400]}`,
  borderRadius: '4px',
  backgroundColor: active
    ? theme.palette.primary.veryLight
    : theme.palette.grey[50],
  paddingTop: `calc(${theme.spacing(1.25)} - 1px)`,
  paddingBottom: `calc(${theme.spacing(1.25)} - 1px)`,
  paddingLeft: `calc(${theme.spacing(2)} - 1px)`,
  paddingRight: `calc(${theme.spacing(2)} - 1px)`,
}));

const TextPrimaryIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

const getLabel = (option) => option?.title || option?.label;

function LabelAndDescription(props) {
  const {
    label,
    description,
    maxWidth,
    children,
  } = props;
  return (
    <Grid container direction='column' spacing={1}>
      <Grid item>
        <Grid container direction='row' flexWrap='noWrap' spacing={8} alignItems='center'>
          <Grid item xs>
            <Typography variant='body1' fontSize={{ xs: '16px', md: '20px' }} fontWeight={700}>
              {label}
            </Typography>
          </Grid>
          {children && (
            <Grid item>
              {children}
            </Grid>
          )}
        </Grid>
      </Grid>
      {description && (
        <Grid item>
          <Box maxWidth={maxWidth}>
            <Typography variant='body2' fontSize='14px' color='text.disabled'>
              {description}
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
}

export default function FiltersSelector(props) {
  const {
    main = false,
    name,
    label,
    description,
    shortDescription,
    twoColumn = false,
    onChange: rawOnChange,
    onClose,
    value: rawValue,
    childValue: rawChildValue,
    options = {},
    analytics = false,
  } = props;
  const [expanded, setExpanded] = useState(true);

  const [primaryValue, childValue] = useMemo(() => {
    if (options.array) return [rawValue || [], rawChildValue || []];
    if (options.boolean) return [Boolean(rawValue)];
    return rawValue;
  }, [options.array, options.boolean, rawChildValue, rawValue]);

  const onChangePrimary = (value) => rawOnChange({ target: { name, value } });
  const onChangeChild = (value) => rawOnChange({
    target: {
      name: options?.childFilters?.name,
      value,
    },
  });

  const handleToggleSelect = (toggledValue) => () => {
    const toggledOption = options.array.find((option) => option.value === toggledValue);
    if (toggledOption) {
      const childrenValues = toggledOption?.children?.map((child) => child.value);
      const childIsChecked = childrenValues?.some((value) => childValue.includes(value));
      if (primaryValue.includes(toggledValue) || childIsChecked) {
        onChangePrimary(primaryValue.filter((value) => value !== toggledValue));
        if (toggledOption.children) {
          onChangeChild(childValue.filter((value) => !childrenValues.includes(value)));
        }
      } else {
        onChangePrimary([...primaryValue, toggledValue]);
        if (toggledOption.children) {
          // ensure children are not duplicated
          const cleanedChildValue = childValue.filter((value) => !childrenValues.includes(value));
          onChangeChild([...cleanedChildValue, ...childrenValues]);
        }
      }
    } else {
      const parent = options.array.find((option) => (
        option.children.map((child) => child.value).includes(toggledValue)
      ));
      const parentChecked = primaryValue.includes(parent.value);
      if (childValue.includes(toggledValue)) {
        onChangeChild(childValue.filter((value) => value !== toggledValue));
        // uncheck parent when unchecking children
        if (parentChecked) {
          onChangePrimary(primaryValue.filter((value) => value !== parent.value));
        }
      } else {
        const newChildValue = [...childValue, toggledValue];
        onChangeChild(newChildValue);
        const worthChecking = newChildValue.length >= parent.children.length;
        const childWillBeChecked = (child) => newChildValue.includes(child.value);
        // check parent when checking all children
        if (!parentChecked && worthChecking && parent.children.every(childWillBeChecked)) {
          onChangePrimary([...primaryValue, parent.value]);
        }
      }
    }
  };

  const handleToggleSwitch = (e) => {
    const { checked } = e.target;
    if (analytics) {
      analyticsLib.track(`filterIncentives-${name}-${checked ? 'on' : 'off'}`);
    }
    onChangePrimary(checked);
    if (main) {
      onClose();
    }
  };

  if (main) {
    if (options.array) {
      const allOptions = options.array.flatMap((opt) => (
        opt?.children ? [opt, ...opt.children] : [opt]
      ));
      return (
        <SelectField
          color='primary'
          size='small'
          multiple
          fullWidth
          native={false}
          name={name}
          label={label}
          onClose={onClose}
          value={[...primaryValue, ...childValue]}
          renderValue={(renderValue) => (
            renderValue.map((item) => (
              getLabel(allOptions.find((opt) => opt.value === item))
            )).join(', ')
          )}
        >
          {options.array.map((opt) => (
            <OptionCheckbox
              key={opt.value}
              mode='select'
              option={opt}
              variant={options.variant}
              primaryValue={primaryValue}
              childValue={childValue}
              onToggle={handleToggleSelect}
            />
          ))}
        </SelectField>
      );
    }
    if (options.boolean) {
      return (
        <OutlinedInputBox active={primaryValue}>
          <Grid
            container
            flexWrap='nowrap'
            alignItems='center'
            justifyContent='space-between'
            columnSpacing={1.5}
          >
            <Grid item sx={{ overflowX: 'hidden' }}>
              <TruncatedTypog variant='body2' color={primaryValue ? 'textPrimary' : 'textSecondary'}>
                {shortDescription || description}
              </TruncatedTypog>
            </Grid>
            <Grid item display='flex' alignItems='center'>
              <Switch
                variant='contained'
                name={name}
                color='secondary'
                offColor='grey.700'
                checked={primaryValue}
                onChange={handleToggleSwitch}
              />
            </Grid>
          </Grid>
        </OutlinedInputBox>
      );
    }
  }

  if (options.array) {
    const renderedOptions = options.array.map((opt) => (
      <Grid
        item
        xs={options.variant === 'tile' ? 6 : 12}
        md={(options.variant === 'tile' || twoColumn) ? 6 : 'auto'}
        key={opt.value}
      >
        <OptionCheckbox
          mode='inline'
          option={opt}
          variant={options.variant}
          primaryValue={primaryValue}
          childValue={childValue}
          onToggle={handleToggleSelect}
        />
      </Grid>
    ));
    return (
      <div>
        <LabelAndDescription
          label={label}
          description={description}
          maxWidth='max(75%, 180px)'
        >
          <TextPrimaryIconButton onClick={() => setExpanded((bool) => !bool)}>
            {expanded ? <Remove /> : <Add />}
          </TextPrimaryIconButton>
        </LabelAndDescription>
        <Collapse in={expanded}>
          {
            options.variant === 'tile' ? (
              <Box pt={1.5}>
                <Grid container spacing={2}>
                  {renderedOptions}
                </Grid>
              </Box>
            ) : (
              <Box px={2} pt={1.5}>
                <Grid container spacing={{ xs: 0, md: twoColumn ? 0 : 2 }}>
                  {renderedOptions}
                </Grid>
              </Box>
            )
          }
        </Collapse>
      </div>
    );
  }
  if (options.boolean) {
    return (
      <LabelAndDescription
        label={label}
        description={description}
        maxWidth='max(75%, 180px)'
      >
        <Switch
          name={name}
          color='secondary'
          offColor='grey.700'
          variant='contained'
          checked={primaryValue}
          onChange={handleToggleSwitch}
        />
      </LabelAndDescription>
    );
  }
}
